import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../../api/clientInstance";
import { resolveAwayColor } from "../../utils/colors";
import GamePlayer from "../../components/GamePlayer";
import GameIntro from "../../components/GameIntro";
import "./MatchPage.css";

export default function MatchPage() {
  const { t }        = useTranslation();
  const { matchId }  = useParams();
  const navigate     = useNavigate();
  const location     = useLocation();

  // Go back to where we came from (e.g. the cup page). Fall back to the
  // dashboard when the viewer was opened directly (no in-app history).
  const goBack = () => {
    if (location.key !== "default") navigate(-1);
    else navigate("/dashboard");
  };
  const [match, setMatch]       = useState(null);
  const [error, setError]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [showIntro, setShowIntro] = useState(true);
  const [ev, setEv]             = useState(null);   // current match event (shown above the pitch)

  useEffect(() => {
    api(`/game/match/${matchId}`)
      .then(setMatch)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [matchId]);


  // Deliver the result to GameIntro as soon as it's available so the
  // intro transitions to the player right after its animation finishes.
  useEffect(() => {
    if (match && showIntro) GameIntro.deliver(match);
  }, [match, showIntro]);


  if (loading) return <p className="match-loading">{t("match.loading")}</p>;
  if (error || !match) return <p className="match-error">{error ?? t("match.notFound")}</p>;

  const localColor = match.local_color ?? "#e07b00";
  const awayColor  = match.away_color  ?? "#3b82f6";
  // Same color the pitch uses for the away side: swaps to the away kit when the
  // two teams' primary colors clash, so the away name/dot match the players.
  const awayDisplayColor = resolveAwayColor(localColor, awayColor, match.away_alt_color ?? "#a855f7");

  return (
    <div className="match-page">
      <div className="match-header">
        <button className="match-back" onClick={goBack} title={t("match.back")}>←</button>
        <div className="match-teams">
          <div className="match-team-label">
            <span className="match-team-name" style={{ color: localColor }}>{match.local_team}</span>
            {match.local_manager && <span className="match-coach">{t("game.coach")} {match.local_manager}</span>}
          </div>
          <span className="match-vs">VS</span>
          <div className="match-team-label match-team-label--right">
            <span className="match-team-name" style={{ color: awayDisplayColor }}>{match.away_team}</span>
            {match.away_manager && <span className="match-coach">{t("game.coach")} {match.away_manager}</span>}
          </div>
        </div>
        <div className="match-back-spacer" />
        <div className="match-event">
          {ev && (
            <>
              <span className="match-event-dot" style={{ background: ev.team === 0 ? localColor : awayDisplayColor }} />
              <span className="match-event-text">{ev.label} {t(`game.ev${ev.type}`)}</span>
            </>
          )}
        </div>
      </div>

      {showIntro ? (
        <GameIntro
          localTeam={{ name: match.local_team, localColor }}
          opponent={{ name: match.away_team, starters: match.away_starters ?? [] }}
          localPlayers={match.local_starters ?? []}
          awayColor={awayColor}
          awayAltColor={match.away_alt_color ?? "#a855f7"}
          onDone={() => setShowIntro(false)}
        />
      ) : (
        <GamePlayer
          frames={match.frames}
          localColor={localColor}
          awayColor={awayColor}
          awayAltColor={match.away_alt_color ?? "#a855f7"}
          perspective={match.perspective ?? "local"}
          localPlayerNames={match.local_player_names ?? []}
          awayPlayerNames={match.away_player_names ?? []}
          autoPlay
          onEvent={setEv}
        />
      )}
    </div>
  );
}
